import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  BookOpen, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Plus,
  ClipboardList,
  FileSpreadsheet,
  BarChart3,
  Activity,
  Loader2,
  Database,
  Map
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: "/", label: "Tableau de bord", icon: LayoutDashboard },
  { path: "/realtime", label: "Temps réel", icon: Activity },
  { path: "/new-quote", label: "Nouveau devis", icon: Plus },
  { path: "/excel-fill", label: "Chiffrage Excel", icon: FileSpreadsheet },
  { path: "/plan-analysis", label: "Analyse de plans", icon: Map },
  { path: "/pricing-stats", label: "Statistiques prix", icon: BarChart3 },
  { path: "/projects", label: "Projets", icon: FolderOpen },
  { path: "/supplier-tracking", label: "Suivi fournisseurs", icon: ClipboardList },
  { path: "/knowledge", label: "Base de connaissances", icon: BookOpen },
  { path: "/suppliers", label: "Fournisseurs", icon: Users },
  { path: "/settings", label: "Paramètres", icon: Settings },
];

// Composant indicateur d'extraction dans la sidebar (ancien)
function ExtractionIndicator() {
  return null; // Désactivé - déplacé vers ExtractionBanner
}

// Composant toast d'extraction
function ExtractionBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [lastCompletedAt, setLastCompletedAt] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/api/pricing/extraction-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        setStatus(data);
        
        // Réafficher seulement si une NOUVELLE extraction démarre
        // (pas la même qui était déjà terminée)
        if (data?.status === "running") {
          setDismissed(false);
        }
        
        // Mémoriser quand l'extraction se termine pour ne pas réafficher
        if (data?.status === "completed" && data?.completed_at) {
          if (lastCompletedAt !== data.completed_at) {
            setLastCompletedAt(data.completed_at);
          }
        }
      } catch (error) {
        // Silencieux en cas d'erreur
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [user, lastCompletedAt]);

  // Ne pas afficher si:
  // - Pas de statut
  // - L'extraction n'est pas en cours (completed, idle, etc.)
  // - L'utilisateur a fermé le badge
  if (!status || status.status !== "running" || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-primary text-primary-foreground px-6 py-3 rounded-full shadow-lg flex items-center gap-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm font-medium">Extraction en cours</span>
        <span className="text-sm opacity-80 max-w-xs truncate">
          {status.current_file || "Analyse..."}
        </span>
        <div className="flex items-center gap-2">
          <div className="w-20 h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${status.percent || 0}%` }}
            />
          </div>
          <span className="text-sm font-bold">{(status.percent || 0).toFixed(1)}%</span>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="ml-2 opacity-60 hover:opacity-100 transition-opacity"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-lg"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6 text-gray-600" />
        </button>
        <img 
          src="https://customer-assets.emergentagent.com/job_quote-genius-vert/artifacts/yaw6rlun_LOGO-Braux-300px-email.png" 
          alt="Braux" 
          className="h-8"
        />
        <div className="w-10" />
      </div>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-gray-100 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <img 
                src="https://customer-assets.emergentagent.com/job_quote-genius-vert/artifacts/yaw6rlun_LOGO-Braux-300px-email.png" 
                alt="Braux Paysagistes" 
                className="h-12"
              />
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">
              Menu
            </p>
            {navItems.slice(0, 8).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={isActive ? "sidebar-item-active" : "sidebar-item"}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            <p className="px-4 py-2 mt-6 text-xs font-medium uppercase tracking-wider text-gray-400">
              Général
            </p>
            {navItems.slice(8).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={isActive ? "sidebar-item-active" : "sidebar-item"}
                  data-testid={`nav-${item.path.replace("/", "")}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Indicateur d'extraction en cours */}
            <ExtractionIndicator />
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-[#1E8B5A] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start gap-3 text-gray-600 hover:text-red-600 hover:bg-red-50 mt-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span>Déconnexion</span>
            </Button>
            
            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center text-xs text-gray-400">
                <a 
                  href="https://nexus-conseil.ch" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-[#1E8B5A] transition-colors"
                >
                  made with ❤️ by nexus
                </a>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-10">
          {children}
        </div>
        {/* Extraction toast at bottom center */}
        <ExtractionBanner />
      </main>
    </div>
  );
}
